import { stringify } from 'query-string';
import { useQuery, UseQueryResult } from 'react-query';
import {
  flowIdCompanyScanBE,
  flowIdCompanyScanNL,
} from '../../../../../../constants/flowIds';
import { request } from '../../../../../../../../common/src/legacy/services/httpSessionService';
import {
  ErrorResponse,
  isErrorResponse,
} from '../../../../../../../../../types/API';
import { useFlowsRouteParams } from '../../../../../../hooks/useFlowsRouteParams';
import useSessionId from '../../../../../../hooks/useSessionId';
import { AskLaterQuestionList } from '../types';
import { useConversation } from '../../../../../../../../conversations/src/features/detail/shared/api/getConversationById';
import { useFlowsSelector } from '../../../../../../../../common/src/utils/globalVariables';
import usePartyId from '../../../../../../../../common/src/hooks/query-params/usePartyId';
import { isSocialLoginUrl } from '../../../../../../../../app/src/features/authentication/constants/SocialAuth';
import useIsNLplan from '../../../../../../../../common/src/hooks/useIsNLPlan';

interface GetAskLaterQuestionsParams {
  party_id: string;
  flow_id: string;
}

export const getUseAskLaterQuestionQKey = ({
  party_id,
  flow_id,
}: GetAskLaterQuestionsParams): string[] => {
  return ['company-scan', 'flows-ask-later-questions', party_id, flow_id];
};

export const getAskLaterQuestions = async (
  params: GetAskLaterQuestionsParams,
): Promise<[AskLaterQuestionList, number]> => {
  const path = `v1/api/flows-ask-later-questions`;
  const qString = stringify(params);
  const pathWithParams = `${path}?${qString}`;

  const [resp, status] = await request({ path: pathWithParams, method: 'GET' });
  return [resp, status];
};

export const getAskLaterQuestionsQueryFn = async ({
  party_id,
  flow_id,
}: Partial<GetAskLaterQuestionsParams>): Promise<AskLaterQuestionList> => {
  if (!party_id || !flow_id) throw new Error();

  const [resp] = await getAskLaterQuestions({
    party_id,
    flow_id,
  });

  if (isErrorResponse(resp)) {
    /**
     * If no questions were added yet make sure that we return an empty array instead
     */
    if (resp.msg === 'FLOW_FILTER_NOT_FOUND') {
      return {
        questions: [],
      };
    }

    throw new Error();
  }

  return resp;
};

const useRouteParams = () => {
  const { isNL } = useIsNLplan();
  const { sessionId: routeSessionId, flowId: routeFlowId } =
    useFlowsRouteParams();
  const qSessionId = useSessionId();

  let flowId;
  if (isNL) flowId = flowIdCompanyScanNL;
  else flowId = flowIdCompanyScanBE;

  return {
    sessionId: routeSessionId || qSessionId,
    flowId: routeFlowId || flowId,
  };
};

interface Props {
  flowId?: string;
  partyId?: string;
  isEnabled?: boolean;
}

export const useAskLaterQuestions = ({
  flowId: flowIdProp,
  partyId: partyIdProp,
  isEnabled = true,
}: Props = {}): UseQueryResult<AskLaterQuestionList> => {
  const { isNL } = useIsNLplan();
  const isViewModeOnly = useFlowsSelector(
    (state) => state.flows.isViewOnlyModeOn,
  );
  const partyIdParam = usePartyId();

  // eslint-disable-next-line prefer-const
  let { sessionId, flowId } = useRouteParams();

  if (!flowId && flowIdProp) flowId = flowIdProp;
  if (!flowId) flowId = isNL ? flowIdCompanyScanNL : flowIdCompanyScanBE;
  const { data } = useConversation(sessionId, {
    refetchOnMount: false,
    enabled: !isViewModeOnly && isEnabled,
  });

  const partyId = partyIdProp || partyIdParam || data?.prospect?.id;

  return useQuery<AskLaterQuestionList, ErrorResponse>(
    ['company-scan', 'flows-ask-later-questions', partyId, flowId],
    () => getAskLaterQuestionsQueryFn({ party_id: partyId, flow_id: flowId }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // This query will never happen for a campaign so we make sure its disabled
      enabled: !isSocialLoginUrl && isEnabled && (!!partyId || !!flowId),
    },
  );
};
